import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { AppContainer } from 'react-hot-loader';
import { initializeIcons } from 'office-ui-fabric-react/lib/Icons';
import { log } from 'util';
import * as moment from 'moment';
import { clientLog, serverLog, apiLink, apiGetAccount, LSKEY_FORCEINTERACTIVELOGIN } from './util';
import { BrowserAuthError, createNestablePublicClientApplication } from "@azure/msal-browser";
import { createLocalUrl } from "./util";
import { initTheme, LSKEY_THEMEMODE } from './theme';

import App from './components/App';
import Debugger from './components/Debugger';

import 'office-ui-fabric-react/dist/css/fabric.min.css';
import './styles.less';

const title = 'MailMerge365';

let isOfficeInitialized = false;
let initError = null;
let initEx = null;
let apiLinked = false;
let initMustSetup = false;
let exchangeItemID = null;
let userIdentityToken = null;
let userAccessToken = null;

// Simple client-side loop breaker / throttle for auth attempts
let authInProgress = false;
let authAttemptCount = 0;
let lastAuthAttemptAt = 0;
const AUTH_WINDOW_MS = 60 * 1000; // 1 minute
const MAX_AUTH_ATTEMPTS_IN_WINDOW = 2;

function tooManyAuthAttemptsNow(): boolean {
    const now = Date.now();
    if (now - lastAuthAttemptAt > AUTH_WINDOW_MS) {
        authAttemptCount = 0;
    }
    return authAttemptCount >= MAX_AUTH_ATTEMPTS_IN_WINDOW;
}

function recordAuthAttempt() {
    const now = Date.now();
    if (now - lastAuthAttemptAt > AUTH_WINDOW_MS) {
        authAttemptCount = 0;
    }
    authAttemptCount++;
    lastAuthAttemptAt = now;
}

function breakAuthLoop(message: string, extra?: any) {
    // Best effort to clear all local state when loop detected
    clearAllLocalState();

    initError = message;
    initEx = extra ? JSON.stringify(extra) : null;
    render(App);
}

function getUrlParameter(name) {
    name = name.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]');
    var regex = new RegExp('[\\?&]' + name + '=([^&#]*)');
    var results = regex.exec(location.search);
    return results === null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '));
};

function getCookie(name) {
    var match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    if (match) {
        return match[2];
    }
    return null;
}

function setCookie(cname, cvalue) {
    var d = new Date();
    d.setTime(d.getTime() + (30 * 24 * 60 * 60 * 1000));
    var expires = "expires=" + d.toUTCString();
    document.cookie = cname + "=" + cvalue + ";" + expires + ";secure=true;path=/;SameSite=Lax;";
}

function clearCookies() {
    try {
        const cookies = document.cookie ? document.cookie.split(';') : [];
        for (let i = 0; i < cookies.length; i++) {
            const cookie = cookies[i];
            const eqPos = cookie.indexOf('=');
            let name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
            name = name.trim();
            if (name.length > 0) {
                // Expire at root path
                document.cookie = name + "=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax; secure=true;";
                // Also try current path
                document.cookie = name + "=; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax; secure=true;";
            }
        }
    } catch { }
}

function shouldForceInteractiveLogin(): boolean {
    try { return localStorage.getItem(LSKEY_FORCEINTERACTIVELOGIN) === "1"; } catch { return false; }
}

function clearForceInteractiveLogin() {
    try { localStorage.removeItem(LSKEY_FORCEINTERACTIVELOGIN); } catch { }
}

function clearLocalStores() {
    // Keep the theme preference and the force-interactive-login flag across resets
    let themeMode = null;
    let forceInteractiveLogin = null;
    try { themeMode = localStorage.getItem(LSKEY_THEMEMODE); } catch { }
    try { forceInteractiveLogin = localStorage.getItem(LSKEY_FORCEINTERACTIVELOGIN); } catch { }

    try { sessionStorage.clear(); } catch { }
    try { localStorage.clear(); } catch { }

    try { if (themeMode) localStorage.setItem(LSKEY_THEMEMODE, themeMode); } catch { }
    try { if (forceInteractiveLogin) localStorage.setItem(LSKEY_FORCEINTERACTIVELOGIN, forceInteractiveLogin); } catch { }

    // Remove common MSAL stuck keys defensively
    try {
        const removeMsalLike = (storage: Storage) => {
            const keys: string[] = [];
            for (let i = 0; i < storage.length; i++) {
                const k = storage.key(i);
                if (!k) continue;
                if (k.startsWith('msal.') || k.indexOf('interaction') >= 0) {
                    keys.push(k);
                }
            }
            keys.forEach(k => storage.removeItem(k));
        };
        removeMsalLike(sessionStorage);
        removeMsalLike(localStorage);
    } catch { }
}

function clearRoamingSettings() {
    try {
        // Remove our known key to let auto sign-in re-enable on fresh start
        const LSKEY_AUTOSIGNINDISABLED = 'mm365-autosignindisabled';
        Office.context.roamingSettings.remove(LSKEY_AUTOSIGNINDISABLED);
        Office.context.roamingSettings.saveAsync(() => { });
    } catch { }
}

function clearAllLocalState() {
    clearCookies();
    clearLocalStores();
    clearRoamingSettings();
}

const onReset = () => {
    clientLog("Reseting")
    initEx = null;
    initError = null;
    initMustSetup = false;
    apiLinked = false;
    exchangeItemID = null;
    userIdentityToken = null;
    userAccessToken = null;
    authAttemptCount = 0;
    lastAuthAttemptAt = 0;
    authInProgress = false;

    clearAllLocalState();
    
    render(App);
    onSignIn();
}

const render = (Component) => {
    ReactDOM.render(
        <AppContainer>
            <Component
                onReset={onReset}
                apiLinked={apiLinked}
                title={title}
                itemID={exchangeItemID}
                userIdentityToken={userIdentityToken}
                isOfficeInitialized={isOfficeInitialized}
                initError={initError}
                initEx={initEx}
                initMustSetup={initMustSetup}
            />
        </AppContainer>,
        document.getElementById('container')
    );
};

const defaultInitErrorMessage = "There was an error starting the plugin. Client could not get token.";

const handleErrors = (method: string, result: Office.AsyncResult<any>, action: () => void) => {
    if (result.status != Office.AsyncResultStatus.Succeeded) {
        initError = defaultInitErrorMessage;
        render(App);
    }
    else {
        try {
            clientLog(`${method}: OK`);
            action();
        }
        catch (ex) {
            initError = defaultInitErrorMessage;
            render(App);
        }
    }
}

let pca = undefined;
const onSignIn = async (forceTokenRefresh: boolean = false) => {

    if (authInProgress) {
        clientLog("Auth already in progress; ignoring call");
        return;
    }
    if (tooManyAuthAttemptsNow()) {
        clientLog("Too many auth attempts; breaking loop.");
        breakAuthLoop("We detected repeated sign-in attempts. Please close and reopen the add-in or press Ctrl/Cmd+R to refresh. If it persists, disable popup blockers and try again.");
        return;
    }

    authInProgress = true;
    recordAuthAttempt();

    try {

        userIdentityToken = null;

        clientLog("Starting")

        render(App);

        let idToken = null;

        // Initialize the public client application
        pca = await createNestablePublicClientApplication({
            auth: {
                clientId: "d87015f3-71a7-42c3-80cb-5e0f636703ae",
                authority: "https://login.microsoftonline.com/organizations"
            },

        });


        const tokenRequest = {
            scopes: ["openid", "profile", "User.Read"], // copied in fallback
        };

        // After an explicit sign-out, skip silent auth and show the account
        // picker so the user can sign in with a different account
        const forceInteractive = shouldForceInteractiveLogin();

        if (forceInteractive) {
            clientLog("[MSAL] User signed out previously, forcing interactive login.");
        } else {
            try {
                clientLog("[MSAL] Trying to acquire token silently...");
                let userAccount = await pca.acquireTokenSilent(forceTokenRefresh ? { ...tokenRequest, forceRefresh: true } : tokenRequest);

                if (!forceTokenRefresh && isIdTokenExpiredOrExpiring(userAccount.idToken)) {
                    clientLog("[MSAL] Cached ID token is expired or about to expire, retrying with forceRefresh.");
                    userAccount = await pca.acquireTokenSilent({ ...tokenRequest, forceRefresh: true });
                }

                if (isIdTokenExpiredOrExpiring(userAccount.idToken)) {
                    clientLog("[MSAL] ID token still expired after refresh, falling back to interactive login.");
                } else {
                    clientLog("[MSAL] Acquired token silently.");
                    idToken = userAccount.idToken;
                }
            } catch (error) {
                clientLog(`Unable to acquire token silently: ${JSON.stringify(error)}` /* this is not a client error */);
            }
        }

        if (!idToken) {
            // Acquire token silent failure. Send an interactive request via popup.
            try {
                clientLog("[MSAL] Trying to acquire token interactively...");
                const userAccount = await pca.acquireTokenPopup(forceInteractive ? { ...tokenRequest, prompt: "select_account" } : tokenRequest);
                clientLog("[MSAL] Acquired token interactively.");
                idToken = userAccount.idToken;
            } catch (popupError) {

                // Fallback to Dialog API for common popup/monitor issues
                const code = (popupError as any)?.errorCode || (popupError as any)?.error || "";
                const isPopupIssue = popupError instanceof BrowserAuthError ||
                    ["popup_window_error", "popup_closed_error", "monitor_window_timeout", "user_cancelled", "interaction_in_progress"].some(c => (code || "").indexOf(c) >= 0);

                if (isPopupIssue) {
                    try {
                        idToken = await getTokenWithDialogApi();
                    } catch (dialogErr) {
                        clientLog("getTokenWithDialogApi failed: " + (dialogErr?.message || dialogErr));
                        initError = defaultInitErrorMessage;
                        initEx = JSON.stringify({ popupError, dialogErr });
                    }
                } else {
                    // Acquire token interactive failure.
                    clientLog(initError = `Unable to acquire token interactively: ${JSON.stringify(popupError)}`);
                }
            }
        }

        // Log error if both silent and popup (and dialog) requests failed.
        if (!idToken) {
            initError = initError || `Unable to acquire access token.`;
            render(App);
        } else {

            clearForceInteractiveLogin();

            var decoded = decodeJwtExpiration(idToken);
            if (decoded) {
                clientLog("Token received which expires at: " + decoded.expirationDate);
            }
            userIdentityToken = idToken;
            Office.context.mailbox.item.saveAsync(onAfterSaveItem);
        }

    } catch (ex) {
        clientLog("Error onSignIn")
        clientLog(ex)
        initError = defaultInitErrorMessage;
        initEx = JSON.stringify(ex);
        
        render(App);
    } finally {
        authInProgress = false;
    }
};

function decodeJwtExpiration(token) {
    try {
        // Validate input
        if (!token || typeof token !== 'string') {
            clientLog('Invalid token: must be a non-empty string');
            return null;
        }

        // Split the JWT into its three parts
        const parts = token.split('.');
        if (parts.length !== 3) {
            clientLog('Invalid JWT format: must have 3 parts separated by dots');
            return null;
        }

        // Get the payload (middle part)
        const payload = parts[1];

        // Base64url decode the payload
        // Base64url is like base64 but with URL-safe characters
        const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');

        // Add padding if needed
        const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);

        // Decode and parse JSON
        const decodedPayload = JSON.parse(atob(padded));

        // Check if exp claim exists
        if (!decodedPayload.exp) {
            clientLog('Token does not contain an expiration (exp) claim');
            return null;

        }

        // Convert Unix timestamp to JavaScript Date
        const expirationDate = new Date(decodedPayload.exp * 1000);

        // Validate the date
        if (isNaN(expirationDate.getTime())) {
            clientLog('Invalid expiration timestamp');
            return null;
        }

        return {
            expirationDate: expirationDate,
            expirationTimestamp: decodedPayload.exp,
            isExpired: Date.now() > expirationDate.getTime(),
            timeUntilExpiration: expirationDate.getTime() - Date.now(),
            payload: decodedPayload // Return full payload for reference
        };

    } catch (error) {
        clientLog(`Failed to decode JWT expiration: ${error.message}`);
        return null;

    }
}

// Margin matches the server-side clock skew allowance (5 minutes)
const ID_TOKEN_EXPIRY_MARGIN_MS = 5 * 60 * 1000;

// MSAL serves cached results based on the access token lifetime only, so a
// cached ID token can already be expired when acquireTokenSilent succeeds.
// The server rejects expired ID tokens with reAuth, so check before sending.
function isIdTokenExpiredOrExpiring(token: string): boolean {
    const decoded = decodeJwtExpiration(token);
    if (!decoded) {
        // Cannot decode; send it and let the server decide
        return false;
    }
    return decoded.timeUntilExpiration < ID_TOKEN_EXPIRY_MARGIN_MS;
}


/** FALLBACK FOR NAA */
export type AuthDialogResult = {
    idToken?: string;
    error?: string;
};

var _dialogApiResult: Promise<string> | null = null;

type DialogEventMessage = { message: string; origin: string | undefined };
type DialogEventError = { error: number };
type DialogEventArg = DialogEventMessage | DialogEventError;

const getTokenWithDialogApi: () => Promise<string> = () => {
    _dialogApiResult = new Promise((resolve, reject) => {
        Office.context.ui.displayDialogAsync(createLocalUrl(`dialog.html`), { height: 60, width: 30 }, (result) => {
            result.value.addEventHandler(Office.EventType.DialogEventReceived, (arg: DialogEventArg) => {
                const errorArg = arg as DialogEventError;
                if (errorArg.error == 12006) {
                    _dialogApiResult = null;
                    reject("Login window closed");
                }
            });
            result.value.addEventHandler(Office.EventType.DialogMessageReceived, (arg: DialogEventArg) => {
                const messageArg = arg as DialogEventMessage;
                const parsedMessage = JSON.parse(messageArg.message);
                result.value.close();

                if (parsedMessage.error) {
                    reject(parsedMessage.error);
                    _dialogApiResult = null;
                } else if (parsedMessage.idToken) {
                    resolve(parsedMessage.idToken);
                } else {
                    reject("Dialog returned no token");
                    _dialogApiResult = null;
                }
            });
        });
    });
    return _dialogApiResult;
}
/** END FALLBACK FOR NAA */

const onAfterSaveItem = (result: Office.AsyncResult<string>) => {
    handleErrors("onAfterSaveItem", result, () => {
        exchangeItemID = result.value
        Debugger.appendLog("exchangeItemID:" + exchangeItemID);
        linkToApi();
    });
}

const linkToApi = () => {
    try {
        // Ready for deprecation
        var restUrl = "";
        //try {
        //    restUrl = Office.context.mailbox.restUrl;
        //} catch { }

        const pluginEmail = Office?.context?.mailbox?.userProfile?.emailAddress;

        apiLink(
            window.location.href,
            userIdentityToken,
            userAccessToken,
            restUrl,
            Office.context.mailbox.diagnostics.hostName,
            pluginEmail
        )
            .then(onAfterServerLink)
            .catch((ex) => {
                initEx = JSON.stringify({
                    response: ex?.response?.data,
                    axios: ex
                });
                initError = defaultInitErrorMessage;
                render(App);
            })
    }
    catch (ex) {
        initEx = JSON.stringify(ex);
        initError = defaultInitErrorMessage;
        render(App);
    }
}

var retriedAuth = false;
var previousToken = "";
const onAfterServerLink = async (linkResult) => {

    if (!linkResult.reAuth) {
        // The server accepted the identity token (any remaining problem is not
        // an auth loop), so reset the loop-breaker state for future episodes
        authAttemptCount = 0;
        lastAuthAttemptAt = 0;
        retriedAuth = false;
    }

    if (linkResult.success === true) {
        clientLog("onAfterServerLink: OK");
        apiLinked = true;
    }
    else {
        if (linkResult.completeSetup) {
            clientLog("Account problem in onAfterServerLink: " + linkResult.reason);
            initMustSetup = true;
            apiLinked = true;
        }
        else if (linkResult.reAuth) {

            if (!retriedAuth) {
                previousToken = userIdentityToken;
                clientLog("Invalid token in onAfterServerLink: " + linkResult.reason);
                retriedAuth = true;

                clearAllLocalState();

                // Throttle re-auth retry if we've already tried too often in the last minute
                if (tooManyAuthAttemptsNow()) {
                    breakAuthLoop("We detected repeated sign-in attempts. Please close and reopen the add-in or press Ctrl/Cmd+R to refresh. If it persists, disable popup blockers and try again.", linkResult);
                } else {
                    // The server just rejected the cached token, so the retry
                    // must bypass the MSAL/broker cache
                    onSignIn(true);
                }
            } else {

                clientLog("Invalid token twice in onAfterServerLink: " + linkResult.reason);
                initEx = JSON.stringify({
                    ...linkResult, userIdentityToken, previousToken
                });
                initError = "There was a problem signing-in to your account. Re-auth was attempted but failed. " + linkResult.reason;

                if (tooManyAuthAttemptsNow()) {
                    breakAuthLoop("We detected repeated sign-in attempts. Please close and reopen the add-in or press Ctrl/Cmd+R to refresh. If it persists, disable popup blockers and try again.", linkResult);
                } else {
                    recordAuthAttempt();
                    try {
                        userIdentityToken = await getTokenWithDialogApi();
                        if (userIdentityToken) {
                            initEx = null;
                            initError = null;
                            // Use the fresh dialog token directly; restarting
                            // onSignIn would discard it and re-read the stale
                            // cached token
                            Office.context.mailbox.item.saveAsync(onAfterSaveItem);
                        }
                    } catch (ex) {
                        clientLog("getTokenWithDialogApi failed: " + ex?.message);
                    }
                }
            }

        }
        else {
            clientLog("Error in onAfterServerLink: " + linkResult.reason);
            initEx = JSON.stringify(linkResult);
            initError = "There was a problem signing-in to your account. " + linkResult.reason;
        }
    }

    render(App);
}

try {
    var locale = (window.navigator as any).userLanguage || window.navigator.language;
    moment.locale(locale)

    initializeIcons();

    initTheme();

    var utm_source = getUrlParameter("utm_source");
    var utm_medium = getUrlParameter("utm_medium");
    var utm_content = getUrlParameter("utm_content");
    var utm_campaign = getUrlParameter("utm_campaign");
    var utm_term = getUrlParameter("utm_term");
    var referer_qs = getUrlParameter("referer");
    var referer_doc = (document.referrer && encodeURIComponent(document.referrer));

    if (utm_source && utm_source.length > 0) setCookie("utm_source", utm_source);
    if (utm_medium && utm_medium.length > 0) setCookie("utm_medium", utm_medium);
    if (utm_content && utm_content.length > 0) setCookie("utm_content", utm_content);
    if (utm_campaign && utm_campaign.length > 0) setCookie("utm_campaign", utm_campaign);
    if (utm_term && utm_term.length > 0) setCookie("utm_term", utm_term);
    if (referer_qs && referer_qs.length > 0) {
        setCookie("referer", referer_qs);
    } else if ((getCookie('referer') || '').length == 0 && referer_doc && referer_doc.length > 0) {
        setCookie("referer", referer_doc);
    }

    /* Render application after Office initializes */
    Office.initialize = () => { };

    Office.onReady(function (info) {
        isOfficeInitialized = true;
        onSignIn();
    });


    /* Initial render showing a progress bar */
    render(App);

    //if ((module as any).hot) {
    //    (module as any).hot.accept('./components/App', () => {
    //        const NextApp = require('./components/App').default;
    //        render(NextApp);
    //    });
    //}

} catch (ex) {
    var body = document.querySelector("body");
    var errContent = "<div style='padding: 20px; text-align: center; display: flex; flex-direction: column; justify-content: center; align-items: center;'><b>Error:</b><span>" + ex + "</span></div>";
    if (body) {
        body.innerHTML = errContent;
    } else {
        document.write(errContent)
    }
}